import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Alert,
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/ssr/DownloadSimple";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { dayjs } from "@/lib/dayjs";
import type { LeadAnalysis } from "@/hooks/api/use-campaign-requests";
import {
	useAttestCampaignRequest,
	useCampaignRequestLeadAnalysis,
	useDownloadExcludedLeads,
	useUpdateCampaignRequest,
} from "@/hooks/api/use-campaign-requests";
import { CustomDatePicker } from "@/components/customDatePicker/custom-date-picker";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { DocumentList } from "../components";
import { formatDateTime, formatQuantity, marketFlag, marketLabel } from "../constants";
import type { DraftAttestations } from "./attestations";
import { readAttestations } from "./attestations";
import type { StepProps } from "./wizard-shell";
import { StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 4 — leads.
 *
 * Skipping is offered as an equal path rather than as a failure: the quote's variable estimate
 * needs a number even when the list is three weeks away, which is what the expected-contacts field
 * is for. Once a list is actually attached that path is over — the promise of a file is replaced by
 * the file, and the count stops being a guess and becomes what we counted.
 *
 * The reach rate and the call length are the client's figures, informed by our benchmark, and the
 * acknowledgement is captured beside them. That is the whole fix for quoting a connect rate nobody
 * can guarantee — it turns "you said 39%" into "we agreed to estimate at 39%".
 */

const schema = z
	.object({
		expectedContacts: z.string(),
		expectedReachRate: z.string(),
		expectedCallMinutes: z.string(),
		leadsDueDate: z.string().nullable(),
	})
	.superRefine((values, context) => {
		const reach = Number(values.expectedReachRate);
		if (values.expectedReachRate !== "" && (!Number.isFinite(reach) || reach < 0 || reach > 100)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["expectedReachRate"],
				message: "A reach rate is a percentage between 0 and 100",
			});
		}

		const minutes = Number(values.expectedCallMinutes);
		if (values.expectedCallMinutes !== "" && (!Number.isFinite(minutes) || minutes <= 0)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["expectedCallMinutes"],
				message: "An average call is longer than zero minutes",
			});
		}

		const contacts = Number(values.expectedContacts);
		if (values.expectedContacts !== "" && (!Number.isInteger(contacts) || contacts < 0)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["expectedContacts"],
				message: "A whole number of contacts",
			});
		}
	});

type FormValues = z.infer<typeof schema>;

/** A decimal string off the API, as the text field wants it. */
function toField(value: string | number | null | undefined): string {
	if (value === null || value === undefined || value === "") return "";
	return String(value);
}

interface LeadSplitTableProps {
	requestId: string;
	analysis: LeadAnalysis | undefined;
	isPending: boolean;
	isError: boolean;
	onRetry: () => void;
}

/**
 * The rows we read, as we read them.
 *
 * The counts answer "how many"; only the rows answer "did you read *my* file". The phone column
 * leads because it is the one the whole split hangs off, and the number we will actually dial is
 * printed beside the number as they typed it — a mis-guessed column or a wrong default country
 * shows up here in one glance instead of in the first invoice.
 */
function LeadSample({ analysis }: { analysis: LeadAnalysis }): React.JSX.Element | null {
	// Defensive, not decorative: the resource types `sample` as always-present, but a server on an
	// older build answers without it, and dereferencing an absent array here white-screened the whole
	// wizard — and kept doing so on every reload, so the request became unreachable. A payload we did
	// not expect must degrade to showing nothing, never to taking the page down.
	const sample = analysis.sample ?? [];
	if (sample.length === 0) return null;

	// The phone column first, then whatever else the list carries, up to what fits without the
	// table needing to be scrolled to be understood.
	const columns = [
		...(analysis.phoneColumn ? [analysis.phoneColumn] : []),
		...analysis.columns.filter((column) => column !== analysis.phoneColumn).slice(0, 3),
	];

	const manyFiles = analysis.files.filter((file) => file.status === "checked").length > 1;

	return (
		<Box>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				{`The first ${sample.length === 1 ? "row" : `${sample.length} rows`} we will call, as we read them`}
			</Typography>
			<Box sx={{ overflowX: "auto" }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
							{columns.map((column) => (
								<TableCell key={column} sx={{ fontWeight: 600 }}>
									{column}
								</TableCell>
							))}
							<TableCell sx={{ fontWeight: 600 }}>We will dial</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sample.map((row) => (
							<TableRow key={`${row.file}:${row.row}`}>
								<TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
									{manyFiles ? `${row.file} · ${row.row}` : row.row}
								</TableCell>
								{columns.map((column) => (
									<TableCell key={column} sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
										{row.values[column] ?? ""}
									</TableCell>
								))}
								<TableCell sx={{ whiteSpace: "nowrap" }}>{`${marketFlag(row.market)} ${row.phone}`.trim()}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Box>
	);
}

/**
 * "What we found / Rows / Goes to" — UX §07.
 *
 * Note 1 is the whole reason it is here: the split table is the cleaning pipeline made visible,
 * and country code is what routes each lead to its market, so the client reads the routing
 * decision rather than trusting it. Duplicates are matched by normalized phone number across
 * all files, retaining the most complete eligible source row. Names and emails are not unique IDs.
 *
 * Note 2 is the download. Silently dropping rows is how you lose trust; handing them back with a
 * reason on each is how you keep it.
 */
function LeadSplitTable({ requestId, analysis, isPending, isError, onRetry }: LeadSplitTableProps): React.JSX.Element {
	const downloadMutation = useDownloadExcludedLeads();

	if (isPending) {
		return (
			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					<CircularProgress size={18} />
					<Typography variant="body2" color="text.secondary">
						Checking your lists…
					</Typography>
				</Stack>
			</Card>
		);
	}

	// A request that failed is a failure, not an empty list. The file is safe either way, but the
	// count is missing rather than zero, and the retry is the whole of the fix most of the time.
	if (isError || !analysis) {
		return (
			<Alert
				severity="error"
				action={
					<Button color="inherit" size="small" onClick={onRetry}>
						Try again
					</Button>
				}
			>
				{"We could not check these lists just now. They are uploaded and safe — nothing has been counted yet."}
			</Alert>
		);
	}

	const unreadable = analysis.files.filter((file) => file.status === "unreadable");
	const checked = analysis.files.filter((file) => file.status === "checked");

	/**
	 * Nothing was read at all.
	 *
	 * A split table of zeros beside a file that plainly holds thousands of rows reads as a broken
	 * product, so the file says what it could not be opened as and what to send instead. Each
	 * reason comes off the file itself — an unsupported format and a corrupt upload need different guidance, and
	 * do not have the same fix.
	 */
	if (checked.length === 0) {
		return (
			<Alert severity="warning">
				<Stack spacing={0.5}>
					<Typography variant="body2">
						{unreadable.length === 1 ? "We could not read this file." : "We could not read these files."}
					</Typography>
					{unreadable.map((file) => (
						<Typography key={file.documentId} variant="body2" color="text.secondary">
							<strong>{file.filename}</strong> — {file.reason ?? "We could not open it."}
						</Typography>
					))}
				</Stack>
			</Alert>
		);
	}

	// Opened, and genuinely empty. Worth saying plainly: it is almost always the wrong file or the
	// wrong tab, and neither is visible from a table of zeros.
	if (analysis.rowsIn === 0) {
		return (
			<Stack spacing={2}>
				<Alert severity="warning">
					{`We opened ${checked.map((file) => namedFile(file)).join(", ")}, but there are no rows in ${checked.length === 1 ? "it" : "them"}.`}
				</Alert>
				{unreadable.map((file) => (
					<Alert key={file.documentId} severity="warning">
						<strong>{file.filename}</strong>
						{` — ${file.reason ?? "we could not open it."}`}
					</Alert>
				))}
			</Stack>
		);
	}

	return (
		<Card variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				{/* The numbers lead. Everything under them is how they were arrived at. `routedUsable`
				    rather than `usable`: a block whose country code is not one of their markets is
				    printed below as going nowhere, and counting it here as "to call" would have the
				    headline contradicting the table under it. */}
				<Box>
					<Typography variant="h6">
						{`${formatQuantity(analysis.rowsIn)} rows read · ${formatQuantity(analysis.routedUsable)} to call`}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Split by the country code on each number.
					</Typography>
				</Box>

				{/* Only worth splitting out once there is more than one file — with a single list this
				    is the line above, repeated with a filename on it. */}
				{checked.length > 1 ? (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
						{checked.map((file) => (
							<Chip
								key={file.documentId}
								label={`${file.filename} · ${formatQuantity(file.rows)} rows`}
								size="small"
								variant="outlined"
							/>
						))}
					</Box>
				) : null}

				<Box sx={{ overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 600, minWidth: 220 }}>What we found</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="right">
									Rows
								</TableCell>
								<TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Goes to</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{analysis.markets.map((split) => (
								<TableRow key={split.market}>
									<TableCell>
										{split.routed
											? `${marketFlag(split.market)} ${marketLabel(split.market)}${split.callingCode ? ` · ${split.callingCode}` : ""}`
											: `${marketLabel(split.market)}${split.callingCode ? ` · ${split.callingCode}` : ""} — not one of your markets`}
									</TableCell>
									<TableCell align="right">{formatQuantity(split.rows)}</TableCell>
									<TableCell>
										{split.routed ? (
											<Typography variant="body2">
												{marketFlag(split.market)} {split.agentLabel ?? "agent set on step 2"}
											</Typography>
										) : (
											<Typography variant="body2" color="warning.main">
												Nowhere — add the market, or these are not called
											</Typography>
										)}
									</TableCell>
								</TableRow>
							))}

							<TableRow>
								<TableCell>Duplicate numbers removed</TableCell>
								<TableCell align="right">{formatQuantity(analysis.duplicatesRemoved)}</TableCell>
								<TableCell>—</TableCell>
							</TableRow>

							{/* Only when the client asked for it on step 5. The count turns "we will leave
							    those out" into a number they can check before anyone is dialled. */}
							{analysis.excludedNoRecipient > 0 ? (
								<TableRow>
									<TableCell>Left out — nobody to notify</TableCell>
									<TableCell align="right">{formatQuantity(analysis.excludedNoRecipient)}</TableCell>
									<TableCell>
										{`You asked us to leave out contacts with no ${analysis.recipientColumn ?? "recipient"}`}
									</TableCell>
								</TableRow>
							) : null}

							<TableRow>
								<TableCell>Missing or invalid numbers</TableCell>
								<TableCell align="right">{formatQuantity(analysis.rejected)}</TableCell>
								<TableCell>
									{analysis.rejected > 0 ? (
										<Button
											size="small"
											startIcon={<DownloadSimpleIcon />}
											disabled={downloadMutation.isPending}
											onClick={() => downloadMutation.mutate(requestId)}
										>
											Download the excluded rows
										</Button>
									) : (
										"—"
									)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</Box>

				{/* The worked example. Everything above is a claim about the file; this is the file. */}
				<LeadSample analysis={analysis} />

				{/* Where the numbers came from. The download button in the table above is its own
				    explanation — a paragraph restating what the table already lists, row by row, is
				    the table read back out. */}
				{analysis.phoneColumn ? (
					<Typography variant="caption" color="text.secondary">
						{`Numbers read from the “${analysis.phoneColumn}” column${sheetNote(checked)}.`}
					</Typography>
				) : (
					// Rows still came out, so this is not a failure — it is a guess, and one they can
					// overrule by naming the column. Saying "re-upload" over a count that is plainly
					// right would send them back to fix something that worked.
					<Alert severity="warning">
						{`No column is named like a phone number, so we took the first number-looking value in each row. Name it “Phone” and send it again if that is not what you meant.`}
					</Alert>
				)}

				{/* Counted files above, and the ones that never made it into that count below — each
				    with the reason it did not, so "not counted" is never left as a mystery. */}
				{unreadable.map((file) => (
					<Alert key={file.documentId} severity="warning">
						<strong>{file.filename}</strong>
						{` is not counted above — ${file.reason ?? "we could not open it."}`}
					</Alert>
				))}
			</Stack>
		</Card>
	);
}

/**
 * Which tab the rows came from, and only where the workbook had more than one to choose between.
 *
 * The API names the sheet exactly then, so a client whose contacts are on the second tab can see
 * that we read the first — the one mis-read that produces a confident and entirely wrong count.
 */
function namedFile(file: LeadAnalysis["files"][number]): string {
	return file.sheet ? `${file.filename} (the “${file.sheet}” sheet)` : file.filename;
}

function sheetNote(checked: LeadAnalysis["files"]): string {
	const named = checked.filter((file) => file.sheet);
	if (named.length === 0) return "";

	return `, off the ${named.map((file) => `“${file.sheet}” sheet of ${file.filename}`).join(" and ")}`;
}

export function StepLeads({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const updateMutation = useUpdateCampaignRequest();

	const attestMutation = useAttestCampaignRequest();

	/**
	 * Read off the request, never held locally.
	 *
	 * A tick is written the moment it is made, so what is on screen is what is on the record —
	 * there is no state here that could disagree with the API, and a refresh, another machine or a
	 * colleague opening the same draft all show the same two boxes.
	 */
	const attestations = readAttestations(request);

	const {
		control,
		getValues,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isDirty },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			expectedContacts: toField(request.expectedContacts),
			expectedReachRate: toField(request.expectedReachRate),
			expectedCallMinutes: toField(request.expectedCallMinutes),
			leadsDueDate: request.leadsDueDate,
		},
	});

	const hasLeadList = request.documents.some((document) => document.kind === "lead_list");

	/**
	 * Read once, here, and handed down.
	 *
	 * The count the split table leads with is the same count the estimate is built from, so it
	 * cannot be fetched twice and disagree with itself — and it is not fetched at all until there
	 * is a list to analyse.
	 */
	const analysisQuery = useCampaignRequestLeadAnalysis(hasLeadList ? request.id : null);

	/**
	 * How many we will dial, or null where we did not manage to count.
	 *
	 * Null and zero are different answers and the field beside this says so in words, which is
	 * why they cannot be collapsed: `analysisQuery.data?.usable ?? null` read an unreadable
	 * upload as a confident zero, and the estimate then sat there empty under the label
	 * "Contacts to call" and the note "Counted from your list" — a count we had never taken.
	 *
	 * `routedUsable`, not `usable`: numbers whose country code is not one of this request's
	 * markets survive every cleaning rule and are still never dialled.
	 */
	const routed = analysisQuery.data?.routedUsable ?? 0;
	const counted = hasLeadList && routed > 0 ? routed : null;

	/**
	 * Once we have counted the list, the count is the answer.
	 *
	 * Written rather than merely displayed, because the quote's variable line is priced off
	 * `expectedContacts` — leaving the field empty beside a table saying 3,140 would quote zero
	 * minutes of call time.
	 *
	 * Never over a number the client typed themselves: the estimate is their assumption to make,
	 * and a count arriving a second later that silently replaced it would be the file overruling
	 * the person. The ref holds the count it filled rather than a flag, so a second list landing
	 * still pre-fills an empty field, while a field they have cleared on purpose stays cleared.
	 *
	 * The ref is the memory of a fill within one mount, and a list that was already attached when
	 * the step opened has been through it: an empty field beside that list is one the client
	 * emptied and saved. Re-filling it on every revisit would put the number back *and* make an
	 * untouched step write, and a write withdraws the quote.
	 */
	const listPredatesThisVisit = React.useRef(hasLeadList);
	const filled = React.useRef<number | null>(null);
	React.useEffect(() => {
		if (readOnly || counted === null || filled.current === counted) return;
		if (listPredatesThisVisit.current && request.expectedContacts === null) return;
		if (getValues("expectedContacts") !== "") return;

		filled.current = counted;
		setValue("expectedContacts", String(counted), { shouldDirty: true });
	}, [counted, getValues, readOnly, setValue, request.expectedContacts]);

	const contacts = Number(watch("expectedContacts"));
	const reach = Number(watch("expectedReachRate"));
	const minutes = Number(watch("expectedCallMinutes"));

	// The same arithmetic the quote's variable line is built from, shown while they type so the
	// assumption and its consequence are never on different screens.
	const estimate =
		Number.isFinite(contacts) && Number.isFinite(reach) && Number.isFinite(minutes) && contacts > 0 && minutes > 0
			? contacts * (reach / 100) * minutes
			: null;

	// The rail is the one way off this step that writes nothing, so it has to ask first. The
	// attestations are not in this: they are written the moment they are ticked.
	useWizardDirty(isDirty);

	/**
	 * Record a tick, now.
	 *
	 * Its own endpoint, and its own moment: what the client is being asked here is a legal
	 * statement about their contact list, and a statement made on step 4 has to carry the date it
	 * was made on rather than the date of whatever they did four steps later. `/attest` writes the
	 * stamp outside `recompute()`, so ticking a box cannot withdraw a live quote.
	 *
	 * One direction only. A box cannot be unticked once it is stamped — the API fills nulls and
	 * never clears them, so offering an untick would be offering something that does not happen.
	 */
	const tick = (changes: Partial<DraftAttestations>): void => {
		if (changes.leadConsentConfirmed === false || changes.estimatesAcknowledged === false) return;

		void attestMutation.mutateAsync({ id: request.id, data: changes });
	};

	const save = async (values: FormValues): Promise<boolean> => {
		// The attestations are written by their own endpoint the moment they are ticked, so an
		// untouched form has nothing to PUT — and a PUT that changes nothing still withdraws a live
		// quote. See `./dirty`.
		if (!isDirty) return true;

		try {
			await updateMutation.mutateAsync({
				id: request.id,
				data: {
					expectedContacts: values.expectedContacts === "" ? null : Number(values.expectedContacts),
					// Numbers on the way in, decimal strings on the way back out — the one place the two
					// shapes of this request differ.
					expectedReachRate: values.expectedReachRate === "" ? null : Number(values.expectedReachRate),
					expectedCallMinutes: values.expectedCallMinutes === "" ? null : Number(values.expectedCallMinutes),
					// A date we are chasing a file on, cleared by the file arriving. Carried on the next
					// write this step makes rather than forced: a PUT of its own withdraws a live quote,
					// and a promise the client can see has been kept is not worth that.
					leadsDueDate: hasLeadList ? null : values.leadsDueDate,
				},
			});
			return true;
		} catch {
			return false;
		}
	};

	const onContinue = handleSubmit(async (values) => {
		if (await save(values)) goTo(5);
	});

	const onSaveExit = handleSubmit(async (values) => {
		if (await save(values)) exit();
	});

	return (
		<Stack spacing={3}>
			<StepHeading
				title="Who should we call?"
				help={
					hasLeadList
						? "We clean the list and split it by market. Add more files whenever you have them."
						: "Send the list now, or tell us when it's coming — we quote and build the agents either way."
				}
			/>

			<DocumentList
				requestId={request.id}
				documents={request.documents}
				kinds={["lead_list"]}
				filterKinds={["lead_list"]}
				readOnly={readOnly}
				title="Lead lists"
				emptyText="CSV UTF-8 only. All lead files together: up to 25 MiB, 200,000 rows and 2 million cells. Export Excel sheets as CSV before uploading."
			/>

			{hasLeadList ? (
				<LeadSplitTable
					requestId={request.id}
					analysis={analysisQuery.data}
					isPending={analysisQuery.isPending}
					isError={analysisQuery.isError}
					onRetry={() => void analysisQuery.refetch()}
				/>
			) : null}

			<FormControlLabel
				control={
					<Checkbox
						checked={attestations.leadConsentConfirmed}
						disabled={readOnly || Boolean(request.leadConsentConfirmedAt)}
						onChange={(event) => tick({ leadConsentConfirmed: event.target.checked })}
					/>
				}
				label={
					<Box>
						<Typography variant="body2">
							I confirm these contacts have consented to be called and are not on a do-not-call register.
						</Typography>
						{/* The date stamp UX §07 note 3 asks for, printed where the tick was made. */}
						<Typography variant="caption" color="text.secondary">
							{request.leadConsentConfirmedAt
								? `Recorded ${formatDateTime(request.leadConsentConfirmedAt)}`
								: "We record the date you confirm this."}
						</Typography>
					</Box>
				}
			/>

			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={2}>
					<Box>
						<Typography variant="subtitle1">How do you expect this list to perform?</Typography>
						<Typography variant="body2" color="text.secondary">
							{"We estimate your call-time cost from these. Our benchmarks are a reasonable starting point."}
						</Typography>
					</Box>

					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 4 }}>
							<Controller
								control={control}
								name="expectedContacts"
								render={({ field }) => (
									<FormControl fullWidth error={Boolean(errors.expectedContacts)}>
										<TextField
											{...field}
											type="number"
											disabled={readOnly}
											label={counted === null ? "Contacts we should expect" : "Contacts to call"}
											placeholder="e.g. 3,000"
											error={Boolean(errors.expectedContacts)}
											helperText={
												counted === null
													? "However many are coming."
													: "Counted from your list — change it if you need to."
											}
										/>
										{errors.expectedContacts ? (
											<CustomFormHelperError text={errors.expectedContacts.message ?? ""} />
										) : null}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 4 }}>
							<Controller
								control={control}
								name="expectedReachRate"
								render={({ field }) => (
									<FormControl fullWidth error={Boolean(errors.expectedReachRate)}>
										<TextField
											{...field}
											type="number"
											disabled={readOnly}
											label="Expected reach rate"
											error={Boolean(errors.expectedReachRate)}
											helperText="How many actually pick up. Our APAC benchmark: 34–42% for discovery calls."
											slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
										/>
										{errors.expectedReachRate ? (
											<CustomFormHelperError text={errors.expectedReachRate.message ?? ""} />
										) : null}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 4 }}>
							<Controller
								control={control}
								name="expectedCallMinutes"
								render={({ field }) => (
									<FormControl fullWidth error={Boolean(errors.expectedCallMinutes)}>
										<TextField
											{...field}
											type="number"
											disabled={readOnly}
											label="Expected average call length"
											error={Boolean(errors.expectedCallMinutes)}
											helperText="Our APAC benchmark: 1.8–2.6 min for discovery calls."
											slotProps={{ input: { endAdornment: <InputAdornment position="end">min</InputAdornment> } }}
										/>
										{errors.expectedCallMinutes ? (
											<CustomFormHelperError text={errors.expectedCallMinutes.message ?? ""} />
										) : null}
									</FormControl>
								)}
							/>
						</Grid>
					</Grid>

					{estimate === null ? null : (
						<Alert severity="info" variant="outlined">
							{formatQuantity(contacts)} contacts × {formatQuantity(reach)}% reach × {formatQuantity(minutes)} min
							average ≈ <strong>{formatQuantity(Math.round(estimate))} minutes</strong> of call time. Charged per second
							from the moment a call connects.
						</Alert>
					)}

					<FormControlLabel
						control={
							<Checkbox
								checked={attestations.estimatesAcknowledged}
								disabled={readOnly || Boolean(request.estimatesAcknowledgedAt)}
								onChange={(event) => tick({ estimatesAcknowledged: event.target.checked })}
							/>
						}
						label={
							<Box>
								<Typography variant="body2">
									I understand these figures are illustrative only. Actual reach and call length vary with list quality,
									market and how the campaign performs, so the final call-time charge may differ from the estimate.
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{request.estimatesAcknowledgedAt
										? `Recorded ${formatDateTime(request.estimatesAcknowledgedAt)}`
										: "We record the date you acknowledge this."}
								</Typography>
							</Box>
						}
					/>
				</Stack>
			</Card>

			{/*
			 * The promise of a list, and only until there is one.
			 *
			 * A step showing an uploaded file and asking when it is coming is telling the client
			 * something they can see is untrue. The date is what we chase in the file's absence, so
			 * the moment the file arrives it has nothing left to describe — and the wizard's
			 * "I'll send leads later" exit goes with it.
			 *
			 * The heading above already offers both paths; a card repeating that offer is the same
			 * promise twice on one screen.
			 */}
			{hasLeadList ? null : (
				<Card variant="outlined" sx={{ p: 2 }}>
					<Stack spacing={2}>
						<Typography variant="subtitle1">{"Don't have the list yet?"}</Typography>

						<Grid container spacing={2}>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="leadsDueDate"
									render={({ field }) => (
										<FormControl fullWidth>
											<CustomDatePicker
												label="When will you send it?"
												disabled={readOnly}
												value={field.value}
												minDate={dayjs()}
												setValue={field.onChange}
											/>
										</FormControl>
									)}
								/>
							</Grid>
						</Grid>
					</Stack>
				</Card>
			)}

			<WizardFooter
				busy={updateMutation.isPending}
				secondaryLabel={hasLeadList || readOnly ? undefined : "I'll send leads later"}
				onSecondary={hasLeadList || readOnly ? undefined : () => void onContinue()}
				onBack={() => goTo(3)}
				onSaveExit={readOnly ? undefined : () => void onSaveExit()}
				onContinue={readOnly ? () => goTo(5) : () => void onContinue()}
			/>
		</Stack>
	);
}
